import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import apiClient from "../../../utils/apiClient";
import { hasPermission, PERMISSIONS } from "../../../utils/permissions";
import Loader from "../../Loader";

const PLAN_DURATION_OPTIONS = [
  { value: "30", label: "1 Month (30 days)", subscriptionType: "Month" },
  { value: "180", label: "6 Months (180 days)", subscriptionType: "Month" },
  { value: "365", label: "12 Months (365 days)", subscriptionType: "Year" },
];

const EMPTY_FORM = {
  name: "",
  examType: "WAEC",
  numOfQuestions: "Full Access",
  subscriptionType: "Month",
  duration: "30",
  studentCount: 50,
  price: 0,
  description: "",
  isActive: true,
};

const DEFAULT_SUBSCRIPTION_TYPES = ["Day", "Month", "Year"];

const ListSchoolSlotPackages = () => {
  const { user } = useSelector((state) => state.auth || {});
  const canEdit = hasPermission(user, PERMISSIONS.SCHOOLS_EDIT);
  const [packages, setPackages] = useState([]);
  const [examTypes, setExamTypes] = useState(["WAEC", "JAMB", "BECE", "POST UTME"]);
  const [subscriptionTypes, setSubscriptionTypes] = useState(DEFAULT_SUBSCRIPTION_TYPES);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState("");
  const [saving, setSaving] = useState(false);

  const loadPackages = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/admin/school-slot-packages");
      setPackages(Array.isArray(data?.packages) ? data.packages : []);
      if (Array.isArray(data?.examTypes) && data.examTypes.length) {
        setExamTypes(data.examTypes);
      }
      if (Array.isArray(data?.subscriptionTypes) && data.subscriptionTypes.length) {
        setSubscriptionTypes(data.subscriptionTypes);
      }
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to load slot packages."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId("");
  };

  const startEdit = (pkg) => {
    setEditingId(pkg.id);
    setForm({
      name: pkg.name || "",
      examType: pkg.examType || "WAEC",
      numOfQuestions: pkg.numOfQuestions || "Full Access",
      subscriptionType: pkg.subscriptionType || "Month",
      duration: String(pkg.duration || "30"),
      studentCount: pkg.studentCount || 1,
      price: pkg.price || 0,
      description: pkg.description || "",
      isActive: pkg.isActive !== false,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canEdit) return;

    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await apiClient.put(`/admin/school-slot-packages/${editingId}`, form);
      } else {
        await apiClient.post("/admin/school-slot-packages", form);
      }
      resetForm();
      await loadPackages();
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to save slot package."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (pkg) => {
    if (!canEdit) return;
    if (!window.confirm(`Deactivate "${pkg.name}"? Schools will no longer see it.`)) return;

    try {
      await apiClient.delete(`/admin/school-slot-packages/${pkg.id}`);
      await loadPackages();
    } catch (err) {
      setError(
        err?.response?.data?.errMessage ||
          err?.response?.data?.message ||
          "Failed to deactivate package."
      );
    }
  };

  return (
    <Fragment>
      <div className="content-wrapper">
        <section className="content-header">
          <h1>
            School Portal
            <small>Exam Slot Packages</small>
          </h1>
          <ol className="breadcrumb">
            <li>
              <Link to="/admin">
                <i className="fa fa-dashboard" /> Home
              </Link>
            </li>
            <li className="active">Slot Packages</li>
          </ol>
        </section>

        <section className="content">
          {error && (
            <div className="alert alert-danger">
              <button type="button" className="close" onClick={() => setError("")}>
                ×
              </button>
              {error}
            </div>
          )}

          {canEdit && (
            <div className="box box-primary">
              <div className="box-header with-border">
                <h3 className="box-title">
                  {editingId ? "Edit Slot Package" : "Create Slot Package"}
                </h3>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="box-body">
                  <div className="form-group row">
                    <div className="col-md-4">
                      <label>Package Name</label>
                      <input
                        className="form-control"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        placeholder="e.g. WAEC 50 Seats"
                      />
                    </div>
                    <div className="col-md-4">
                      <label>Exam Type</label>
                      <select
                        className="form-control"
                        value={form.examType}
                        onChange={(e) => setForm({ ...form, examType: e.target.value })}
                      >
                        {examTypes.map((exam) => (
                          <option key={exam} value={exam}>
                            {exam}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-2">
                      <label>Student Slots</label>
                      <input
                        type="number"
                        min="1"
                        className="form-control"
                        value={form.studentCount}
                        onChange={(e) =>
                          setForm({ ...form, studentCount: Number(e.target.value) })
                        }
                        required
                      />
                    </div>
                    <div className="col-md-2">
                      <label>Active</label>
                      <select
                        className="form-control"
                        value={form.isActive ? "true" : "false"}
                        onChange={(e) =>
                          setForm({ ...form, isActive: e.target.value === "true" })
                        }
                      >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="numOfQuestions">Number of questions</label>
                      <input
                        type="text"
                        className="form-control"
                        id="numOfQuestions"
                        placeholder="e.g. Full Access"
                        value={form.numOfQuestions}
                        onChange={(e) =>
                          setForm({ ...form, numOfQuestions: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="subscriptionType">Subscription Type</label>
                      <select
                        className="form-control"
                        id="subscriptionType"
                        value={form.subscriptionType}
                        onChange={(e) =>
                          setForm({ ...form, subscriptionType: e.target.value })
                        }
                      >
                        {subscriptionTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group row">
                    <div className="col-md-6">
                      <label htmlFor="duration">Plan duration (must match school portal)</label>
                      <select
                        className="form-control"
                        id="duration"
                        value={
                          PLAN_DURATION_OPTIONS.some((option) => option.value === String(form.duration))
                            ? String(form.duration)
                            : "30"
                        }
                        onChange={(e) => {
                          const selected =
                            PLAN_DURATION_OPTIONS.find((option) => option.value === e.target.value) ||
                            PLAN_DURATION_OPTIONS[0];
                          setForm({
                            ...form,
                            duration: selected.value,
                            subscriptionType: selected.subscriptionType,
                          });
                        }}
                        required
                      >
                        {PLAN_DURATION_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <small className="text-muted">
                        Use the same durations schools select when enrolling: 1 Month, 6 Months, or 12 Months.
                      </small>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="price">Price</label>
                      <input
                        className="form-control"
                        id="price"
                        placeholder="Enter Price"
                        value={form.price}
                        onChange={(e) => setForm({ ...form, price: Number(e.target.value) })}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Description</label>
                    <input
                      className="form-control"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Optional notes for schools"
                    />
                  </div>
                </div>
                <div className="box-footer">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? "Saving..." : editingId ? "Update Package" : "Create Package"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      className="btn btn-default"
                      style={{ marginLeft: 8 }}
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          <div className="box box-info">
            <div className="box-header with-border">
              <h3 className="box-title">Available Packages</h3>
            </div>
            <div className="box-body table-responsive">
              {loading ? (
                <Loader />
              ) : packages.length === 0 ? (
                <p className="text-muted">No slot packages created yet.</p>
              ) : (
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Exam</th>
                      <th>Questions</th>
                      <th>Subscription Type</th>
                      <th>Duration</th>
                      <th>Slots</th>
                      <th>Price</th>
                      <th>Status</th>
                      {canEdit && <th>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map((pkg) => (
                      <tr key={pkg.id}>
                        <td>{pkg.name}</td>
                        <td>{pkg.examType}</td>
                        <td>{pkg.numOfQuestions || "Full Access"}</td>
                        <td>{pkg.subscriptionType || "Month"}</td>
                        <td>
                          {Number(pkg.duration) === 365
                            ? "12 Months (365 days)"
                            : Number(pkg.duration) === 180
                              ? "6 Months (180 days)"
                              : Number(pkg.duration) === 30
                                ? "1 Month (30 days)"
                                : `${pkg.duration || "-"} days`}
                        </td>
                        <td>{pkg.studentCount}</td>
                        <td>₦{Number(pkg.price || 0).toLocaleString()}</td>
                        <td>
                          <span
                            className={`label label-${pkg.isActive ? "success" : "default"}`}
                          >
                            {pkg.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        {canEdit && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-xs btn-warning"
                              onClick={() => startEdit(pkg)}
                            >
                              Edit
                            </button>{" "}
                            {pkg.isActive && (
                              <button
                                type="button"
                                className="btn btn-xs btn-danger"
                                onClick={() => handleDeactivate(pkg)}
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
};

export default ListSchoolSlotPackages;
